import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useQuery } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";

interface FacilityScopePickerProps {
  facilityId?: string;
  onChange: (facilityId: string | undefined) => void;
}

/** Bounded venue lookup; selection changes scope and never grants access. */
export function FacilityScopePicker({
  facilityId,
  onChange,
}: FacilityScopePickerProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), 250);
    return () => clearTimeout(timer);
  }, [input]);
  const results = useQuery({
    queryKey: ["public-facility-search", query],
    queryFn: () => facilityApi.search({ query: query || undefined, limit: 20 }),
    enabled: open,
  });
  const selected = useQuery({
    queryKey: ["public-facility", facilityId],
    queryFn: () => facilityApi.getPublic(facilityId!),
    enabled: Boolean(facilityId),
  });
  const value = facilityId
    ? {
        facility_id: facilityId,
        name: selected.data?.name ?? "Selected facility",
      }
    : null;
  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      options={results.data?.items ?? []}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) =>
        option.facility_id === current.facility_id
      }
      filterOptions={(options) => options}
      onInputChange={(_event, text, reason) => {
        if (reason === "input") setInput(text);
        if (reason === "clear" || reason === "reset") setInput("");
      }}
      onChange={(_event, option) => onChange(option?.facility_id)}
      loading={results.isFetching}
      noOptionsText={
        results.isError
          ? "Facility search unavailable"
          : "No matching facilities"
      }
      clearText="Show all facilities"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Facility"
          placeholder="All facilities"
          error={selected.isError || (open && results.isError)}
          helperText={
            selected.isError
              ? "Facility details unavailable. Clear to show all facilities."
              : undefined
          }
        />
      )}
      sx={{ minWidth: 0, width: "100%", maxWidth: 420 }}
    />
  );
}
